<script setup>
import debounce from 'lodash/debounce';

import { locationTypes } from '@/constants/locations';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },

  placeholder: {
    type: String,
    default: undefined,
  },

  type: {
    type: String,
    default: undefined,
  },
  stationName: {
    type: String,
    default: '',
  },
  oppositeLocationId: {
    type: Number,
    default: undefined,
  },

  withAutofocus: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['select', 'clear']);

const { t } = useI18n();

// Search
const searchStore = useSearchStore();
const search = ref(undefined);

const searchItems = ref([]);
function getSearchItems() {
  searchStore
    .fetchLocations({
      filter: {
        name: search.value,
        oppositeLocationId: props.oppositeLocationId,
      },
    })
    .then((data) => (searchItems.value = data));
}

// Suggestions
const suggestedItems = ref([]);
function getSuggestedItems() {
  if (suggestedItems.value.length) return;

  searchStore
    .fetchLocations({
      filter: {
        oppositeLocationId: props.oppositeLocationId,
        isWithoutStations: true,
      },
    })
    .then((data) => (suggestedItems.value = filterByContinent(data)));
}

// Recent
const recentItems = ref([]);
const recentLocations = useRecentLocations();
function getRecentItems() {
  if (!recentLocations.data.value.length) return;

  searchStore
    .fetchLocations({
      filter: {
        id: recentLocations.get(props.oppositeLocationId).join(','),
      },
    })
    .then((data) => {
      const items = [];
      const cities = filterByContinent(data);

      recentLocations.data.value.forEach((locationId) => {
        cities.forEach((city) => {
          const withoutOppositeLocation = !city.stations.find(
            (station) => station.id === props.oppositeLocationId,
          );

          if (
            city.id === locationId &&
            city.id !== props.oppositeLocationId &&
            withoutOppositeLocation
          ) {
            items.push({ locationType: locationTypes.CITY, ...city });
          } else {
            const { stations, country } = city;

            const station = stations.find(
              (station) =>
                station.id === locationId &&
                station.id !== props.oppositeLocationId &&
                city.id !== props.oppositeLocationId,
            );

            if (!station) return;

            items.push({
              locationType: locationTypes.STATION,
              ...station,
              country,
              cityId: city.id,
            });
          }
        });
      });

      recentItems.value = items;
    });
}

// Utils
const oppositeStationKeys = {
  departure: 'arrivalStation',
  arrival: 'departureStation',
};
function filterByContinent(data) {
  if (
    (!props.modelValue.departureStation && !props.modelValue.arrivalStation) ||
    props.type === 'departure'
  )
    return data;

  let stationFilterKey;

  if (props.modelValue[`${props.type}Station`] && search.value) {
    stationFilterKey = `${props.type}Station`;
  } else if (props.modelValue[oppositeStationKeys[props.type]]) {
    stationFilterKey = oppositeStationKeys[props.type];
  }

  return stationFilterKey
    ? data.filter(
        (item) =>
          item.country.continentGroup ===
          props.modelValue[stationFilterKey].country?.continentGroup,
      )
    : data;
}

// Fetch
watch(
  search,
  debounce((search) => {
    if (search) {
      getSearchItems();
    } else {
      searchItems.value = [];
      getSuggestedItems();
      getRecentItems();
    }
  }, 200),
  { immediate: true },
);
</script>

<template>
  <div class="bundles-station-select">
    <FormFieldInput
      v-model="search"
      class="bundles-station-select__search"
      :placeholder="placeholder"
      name="search"
      theme="purple"
      clearable
      autocomplete="off"
      :autofocus="withAutofocus"
      @clear="emit('clear')"
    />

    <div class="bundles-station-select__groups">
      <template v-if="!search">
        <UiStationGroup
          v-if="recentItems.length"
          class="bundles-station-select__group"
          :name="t('form.recent')"
          icon-name="recent"
          :show-stations="false"
          :items="recentItems"
          @select-station="emit('select', $event)"
        />

        <UiStationGroup
          v-if="suggestedItems.length"
          class="bundles-station-select__group"
          :name="t('form.suggestions')"
          icon-name="suggestions"
          :items="suggestedItems"
          :show-stations="false"
          @select-station="emit('select', $event)"
        />
      </template>

      <UiStationGroup
        v-else
        class="bundles-station-select__group"
        :items="searchItems"
        @select-station="emit('select', $event)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.bundles-station-select {
  display: flex;
  flex-direction: column;

  &__search {
    flex-shrink: 0;
    padding: 0 em(16);
    margin-bottom: em(24);
  }

  &__groups {
    flex-grow: 1;
    padding: 0 em(16);
    overflow: hidden scroll;
  }

  &__group {
    &:not(:last-child) {
      margin-bottom: em(24);
    }
  }
}
</style>
